import { getCursorFromUrl, transformCamelCaseObjectToSnakeCase } from '@helpers'
import { getDefaultPageSize } from '@helpers/cookie'

import { EXPENSE_V2_DEFAULT_SORTING } from '@constants'

import attachmentActions from '../attachment/actions'
import authActions from '../auth/actions'
import filtersActions from '../filters/actions'
import quarantineActions from '../quarantine/actions'
import actions from './actions'
import type { ExpenseFilingResults, ExpenseReducerAction } from './types'

const defaultPageSize = getDefaultPageSize()

//* local helper
function getFilingNumberFromResults(
  { id, filingNumber }: Pick<ExpenseListData, 'id' | 'filingNumber'>,
  results: ExpenseFilingResults
) {
  return results[id] || filingNumber
}

export const initialState: ExpenseStore = {
  details: {
    data: {} as ExpenseDetailsFrontendValues,
    meta: {
      attachment_count: 0,
      partner_has_attachments: false,
    },
    fetched: false,
    error: null,
    loading: false,
    resetFormAfterUpload: false,
    uploading: false,
  },
  expenseTypes: {
    data: [],
    loading: false,
    error: null,
    fetched: false,
  },
  //* V2 list
  list: {
    cursor: null,
    data: [],
    error: null,
    fetched: false,
    loading: false,
    nextPageUrl: null,
    pageSize: defaultPageSize,
    prevPageUrl: null,
    ...EXPENSE_V2_DEFAULT_SORTING,
  },
  kpiCharts: {
    data: undefined,
    error: null,
    fetched: false,
    loading: false,
  },
}

function reducer(state: ExpenseStore = initialState, action: ExpenseReducerAction): ExpenseStore {
  switch (action.type) {
    case authActions.selectCompany.REQUEST:
      return { ...initialState }

    case filtersActions.initExpenseListParamsFromUrl.REQUEST: {
      return {
        ...state,
        list: {
          ...state.list,
          ...action.payload,
        },
      }
    }

    //* v2
    // update filingNumber in list and reset details
    case actions.bulkFilingV2.SUCCESS:
      return {
        ...state,
        details: { ...initialState.details },
        list: {
          ...state.list,
          data: state.list.data.map((expense: ExpenseListData) => ({
            ...expense,
            filingNumber: getFilingNumberFromResults(expense, action.payload),
          })),
        },
      }

    case actions.expenseFiling.SUCCESS: {
      const { id, filing_number } = state.details.data
      return {
        ...state,
        details: {
          ...state.details,
          data: {
            ...state.details.data,
            filing_number: getFilingNumberFromResults({ id, filingNumber: filing_number }, action.payload),
          },
        },
      }
    }

    case actions.updateExpense.SUCCESS:
    case quarantineActions.updateQuarantineInvoice.SUCCESS: {
      const { meta, ...data } = action.payload
      return {
        ...state,
        details: {
          ...state.details,
          data,
          meta,
        },
      }
    }

    // Expense Details
    case actions.uploadExpense.REQUEST:
      return {
        ...state,
        details: {
          ...state.details,
          uploading: true,
          resetFormAfterUpload: Boolean(action.payload.need_classify),
        },
      }

    case actions.clearResetFormAfterUpload.REQUEST:
      return {
        ...state,
        details: {
          ...state.details,
          resetFormAfterUpload: false,
        },
      }

    case quarantineActions.uploadQuarantineInvoice.REQUEST:
      return {
        ...state,
        details: {
          ...state.details,
          uploading: true,
        },
      }

    case actions.uploadExpense.SUCCESS: {
      const { meta, ...data } = action.payload
      return {
        ...state,
        details: {
          ...state.details,
          data: {
            ...state.details.data,
            ...data,
          },
          meta: {
            ...state.details.meta,
            ...meta,
          },
          uploading: false,
        },
      }
    }

    case quarantineActions.uploadQuarantineInvoice.SUCCESS: {
      const { meta, ...data } = action.payload
      return {
        ...state,
        details: {
          ...state.details,
          data,
          meta,
          uploading: false,
        },
      }
    }
    case actions.uploadExpense.FAILURE:
    case actions.cancelUploadExpenseProcess.REQUEST:
    case actions.abortUploadExpense.REQUEST:
    case quarantineActions.uploadQuarantineInvoice.FAILURE:
      return {
        ...state,
        details: {
          ...state.details,
          uploading: false,
        },
      }

    // details
    //* QUARANTINE INVOICE DETAILS
    case actions.fetchExpenseDetails.REQUEST:
    case quarantineActions.fetchQuarantineInvoiceDetails.REQUEST:
      return {
        ...state,
        details: {
          ...initialState.details,
          loading: true,
        },
      }

    case actions.fetchExpenseDetails.SUCCESS:
    case quarantineActions.fetchQuarantineInvoiceDetails.SUCCESS: {
      const { meta, ...data } = action.payload
      return {
        ...state,
        details: {
          ...state.details,
          data,
          meta,
          loading: false,
          fetched: true,
          error: null,
        },
      }
    }

    case actions.fetchExpenseDetails.FAILURE:
    case quarantineActions.fetchQuarantineInvoiceDetails.FAILURE:
      return {
        ...state,
        details: {
          ...state.details,
          loading: false,
          error: action.payload,
        },
      }

    case actions.removeExpenseArtifact.SUCCESS:
      return {
        ...state,
        details: {
          ...state.details,
          data: {
            ...state.details.data,
            artifact: null,
          },
        },
      }

    case actions.bulkTaggingV2.SUCCESS:
    case actions.bulkCategorizationV2.SUCCESS:
    case actions.bulkApprove.SUCCESS:
    case actions.bulkAccountingV2.SUCCESS:
    case actions.bulkFilingV2.FAILURE: //! used as secondary success action in this case
    case actions.clearExpenseDetails.REQUEST:
    case actions.startBulkAccountingExportV2.REQUEST:
      return {
        ...state,
        details: { ...initialState.details },
      }

    //* reset data when change company
    case authActions.selectCompany.SUCCESS:
      return {
        ...state,
        details: { ...initialState.details },
        expenseTypes: { ...initialState.expenseTypes },
      }

    // advanced accounting: status change by accountant
    case actions.callExcludeFromAccountingToggle.SUCCESS:
    case actions.callAccountingCompleteToggle.SUCCESS:
    case actions.callUnlockAccounting.SUCCESS:
      return {
        ...state,
        details: {
          ...state.details,
          data: {
            ...state.details.data,
            links: {
              ...state.details.data.links,
              accounting_complete_toggle: action.payload.links.accountingCompleteToggle
                ? {
                    disabled_reason: action.payload.links.accountingCompleteToggle.disabledReason,
                    link: action.payload.links.accountingCompleteToggle.link,
                  }
                : null,
              accounting_unlock: action.payload.links.accountingUnlock,
              add_exclude_from_accounting: action.payload.links.addExcludeFromAccounting,
              approve_toggle: state.details.data?.links?.approve_toggle ?? null,
              preview_url: state.details.data?.links?.preview_url ?? null,
              remove_exclude_from_accounting: action.payload.links.removeExcludeFromAccounting,
            },
            status: action.payload.status,
          },
        },
      }

    // approve on details
    case actions.expenseApprove.SUCCESS: {
      const meta = transformCamelCaseObjectToSnakeCase(action.payload) as ExpenseDetailsMeta
      return {
        ...state,
        details: {
          ...state.details,
          meta: {
            ...state.details.meta,
            ...meta,
          },
        },
      }
    }

    // expense types
    case actions.fetchExpenseTypes.REQUEST:
      return {
        ...state,
        expenseTypes: {
          ...state.expenseTypes,
          loading: true,
          fetched: false,
        },
      }

    case actions.fetchExpenseTypes.SUCCESS:
      return {
        ...state,
        expenseTypes: {
          ...state.expenseTypes,
          data: action.payload,
          loading: false,
          fetched: true,
          error: null,
        },
      }

    case actions.fetchExpenseTypes.FAILURE:
      return {
        ...state,
        expenseTypes: {
          ...state.expenseTypes,
          loading: false,
          error: action.payload,
        },
      }

    case actions.createExpenseType.SUCCESS:
      return {
        ...state,
        expenseTypes: {
          ...state.expenseTypes,
          data: [action.payload, ...state.expenseTypes.data],
        },
      }

    // meta
    case attachmentActions.increaseAttachmentCount.REQUEST: {
      if (action.payload.documentType === 'expense') {
        return {
          ...state,
          details: {
            ...state.details,
            meta: {
              ...state.details.meta,
              attachment_count: state.details.meta.attachment_count + 1,
            },
          },
        }
      }

      return state
    }

    case attachmentActions.decreaseAttachment.REQUEST: {
      if (action.payload.documentType === 'expense') {
        return {
          ...state,
          details: {
            ...state.details,
            meta: {
              ...state.details.meta,
              attachment_count: state.details.meta.attachment_count - 1,
            },
          },
        }
      }
      return state
    }

    case attachmentActions.updateAttachmentCount.REQUEST: {
      if (action.payload.documentType === 'expense') {
        return {
          ...state,
          details: {
            ...state.details,
            meta: {
              ...state.details.meta,
              attachment_count: action.payload.count,
            },
          },
        }
      }
      return state
    }

    //* V2 API
    case actions.fetchExpenseListV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
          fetched: false,
        },
      }

    case actions.fetchExpenseListByPagingV2.REQUEST:
    case actions.fetchExpenseDetailsByPagingV2.REQUEST: {
      const cursor = getCursorFromUrl(action.payload.url)
      return {
        ...state,
        list: {
          ...state.list,
          cursor,
          loading: true,
          fetched: false,
        },
      }
    }

    case actions.fetchExpenseListV2.SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          data: action.payload.data,
          prevPageUrl: action.payload.previous,
          nextPageUrl: action.payload.next,
          loading: false,
          fetched: true,
          error: null,
          ...(action.payload.isCursorDropped && {
            cursor: action.payload.previousCursor,
          }),
        },
      }

    // only for paging ExpenseListTable w/ cursor pagination
    case actions.fetchExpenseListByPagingV2.SUCCESS: {
      return {
        ...state,
        list: {
          ...state.list,
          data: action.payload.data,
          prevPageUrl: action.payload.previous,
          nextPageUrl: action.payload.next,
          loading: false,
          fetched: true,
          error: null,
        },
      }
    }

    case actions.fetchExpenseListV2.FAILURE:
    case actions.fetchExpenseListByPagingV2.FAILURE:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          error: action.payload,
        },
      }

    case actions.updateOrderV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
          order: action.payload.order,
          orderBy: action.payload.orderBy,
        },
      }

    case actions.updateRowsPerPageV2.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
          pageSize: action.payload.pageSize,
        },
      }

    case filtersActions.toggleExpenseListDateFilter.REQUEST:
    case filtersActions.updateExpenseListFilters.REQUEST:
    case filtersActions.resetExpenseListFilters.REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
        },
        kpiCharts: {
          ...state.kpiCharts,
          error: null,
          loading: true,
        },
      }

    case actions.fetchExpenseCharts.REQUEST:
      return {
        ...state,
        kpiCharts: {
          ...state.kpiCharts,
          error: null,
          loading: true,
          fetched: false,
        },
      }

    case actions.fetchExpenseCharts.SUCCESS:
      return {
        ...state,
        kpiCharts: {
          ...state.kpiCharts,
          data: action.payload,
          loading: false,
          fetched: true,
        },
      }

    case actions.fetchExpenseCharts.FAILURE:
      return {
        ...state,
        kpiCharts: {
          ...state.kpiCharts,
          loading: false,
          error: action.payload,
        },
      }

    case quarantineActions.triggerExpenseUpdateV2.REQUEST:
    case actions.triggerExpenseListUpdate.REQUEST:
    case actions.resetPagination.REQUEST: {
      return {
        ...state,
        list: {
          ...state.list,
          cursor: null,
          loading: true,
          error: null,
        },
      }
    }

    default:
      return state
  }
}

export default reducer
